import React from "react";
import { Link } from "react-router-dom";
import { Building2, FileCheck, Calendar } from "lucide-react";
import SEO from "../components/SEO";

export const CorporationTaxCompliance = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Corporation Tax Compliance Explained (UK 2026)",
    "description": "Complete Corporation Tax compliance guide for UK limited companies. CT600 filing, payment deadlines, accurate accounts, and HMRC requirements.",
    "author": { "@type": "Organization", "name": "Brightson Accounting" },
    "publisher": { "@type": "Organization", "name": "Brightson Accounting" },
    "datePublished": "2026-04-06"
  };

  return (
    <>
      <SEO
        title="Corporation Tax Compliance UK 2026 | CT600 Filing | Brightson"
        description="Corporation Tax compliance for UK limited companies. CT600 filing requirements, payment deadlines, accurate accounts, allowable expenses. Expert Wolverhampton accountants."
        canonical="/blog/corporation-tax-compliance"
        schema={articleSchema}
      />

      <article className="blog-article">
        <header className="article-header">
          <div className="container">
            <div className="article-meta">
              <span className="article-category"><Building2 size={16} /> Staying Compliant</span>
              <span className="article-date">6 April 2026</span>
              <span className="article-read-time">6 min read</span>
            </div>

            <h1>Corporation Tax Compliance Explained (UK 2026)</h1>
            <p className="article-subtitle">
              Everything UK limited companies must know about Corporation Tax compliance — filing, payment, and avoiding HMRC enquiries
            </p>

            <div className="article-author">
              <div className="author-info">
                <strong>Brightson Accounting (FCCA Qualified)</strong>
                <span>Corporation Tax Specialists | Wolverhampton</span>
              </div>
            </div>
          </div>
        </header>

        <section className="article-content">
          <div className="container">
            <div className="article-body">
              
              <p className="article-lead">
                If you run a limited company in Wolverhampton, Birmingham, or anywhere in the West Midlands, Corporation Tax compliance is critical. Mistakes can lead to HMRC enquiries, penalties, and incorrect tax bills.
              </p>

              <p>
                At Brightson Accounting, we help limited companies stay compliant while minimizing their tax bills legally. This guide explains exactly what you need to do.
              </p>

              <div className="info-box">
                <strong>Quick Summary</strong>
                <ul>
                  <li>File CT600 return within 12 months of year-end</li>
                  <li>Pay Corporation Tax within 9 months of year-end</li>
                  <li>Keep accurate accounts for 6 years</li>
                  <li>Only claim allowable business expenses</li>
                  <li>Report all company income to HMRC</li>
                </ul>
              </div>

              <h2>What is Corporation Tax?</h2>

              <p>
                Corporation Tax is the tax limited companies pay on their profits.
              </p>

              <p>
                <strong>Current Rate (2026):</strong>
              </p>
              <ul>
                <li><strong>19%</strong> for profits up to £50,000 (Small Profits Rate)</li>
                <li><strong>25%</strong> for profits over £250,000 (Main Rate)</li>
                <li><strong>Marginal relief</strong> between £50,000 - £250,000 (effective rate 19%-25%)</li>
              </ul>

              <p>
                You pay Corporation Tax on:
              </p>
              <ul>
                <li>Trading profits</li>
                <li>Investment income (interest, dividends)</li>
                <li>Chargeable gains (selling assets)</li>
              </ul>

              <h2>Key Compliance Requirements</h2>

              <h3>1. File a CT600 Return</h3>

              <p>
                Every limited company must file a CT600 (Corporation Tax return) annually, even if you made no profit.
              </p>

              <p>
                <strong>Deadline:</strong> 12 months after your accounting year-end.
              </p>

              <p>
                Example: Year-end 31 March 2026 → File CT600 by 31 March 2027.
              </p>

              <p>
                <strong>What's Included:</strong>
              </p>
              <ul>
                <li>Company accounts (profit & loss, balance sheet)</li>
                <li>Tax computation (calculating taxable profit)</li>
                <li>Details of any tax reliefs or allowances claimed</li>
              </ul>

              <h3>2. Pay Corporation Tax on Time</h3>

              <p>
                <strong>Deadline:</strong> 9 months + 1 day after your accounting year-end.
              </p>

              <p>
                Example: Year-end 31 March 2026 → Pay by 1 January 2027.
              </p>

              <p>
                <strong>Note:</strong> Payment deadline is BEFORE filing deadline. You must estimate and pay tax before submitting the full return.
              </p>

              <p>
                <strong>Late Payment:</strong> Interest charged at 7.75% per year (current rate).
              </p>

              <h3>3. Keep Accurate Records</h3>

              <p>
                HMRC requires you to keep company records for <strong>6 years</strong>.
              </p>

              <p>
                Records must include:
              </p>
              <ul>
                <li>All sales invoices and receipts</li>
                <li>All purchase invoices and receipts</li>
                <li>Bank statements</li>
                <li>Payroll records</li>
                <li>VAT records (if VAT registered)</li>
                <li>Evidence of capital allowances (equipment, vehicles)</li>
              </ul>

              <p>
                Full guide: <Link to="/blog/record-keeping-uk">Record Keeping Requirements for UK Businesses</Link>
              </p>

              {/* Mid-Article CTA */}
              <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
                <h3>💡 Want to see how much tax you could save?</h3>
                <p>Most UK business owners are overpaying without realising it.</p>
                <Link to="/corporation-tax-calculator" className="article-cta-button">
                  👉 Try the Corporation Tax Calculator
                </Link>
              </div>

              <h2>Allowable Expenses (What You Can Claim)</h2>

              <p>
                You can deduct allowable business expenses from your profit before calculating Corporation Tax.
              </p>

              <p>
                <strong>Common Allowable Expenses:</strong>
              </p>
              <ul>
                <li>Office rent and utilities</li>
                <li>Employee salaries and pensions</li>
                <li>Professional fees (accountant, solicitor)</li>
                <li>Business insurance</li>
                <li>Marketing and advertising</li>
                <li>Travel and subsistence (business only)</li>
                <li>Stock and raw materials</li>
                <li>Equipment (via capital allowances)</li>
              </ul>

              <p>
                <strong>NOT Allowable:</strong>
              </p>
              <ul>
                <li>Client entertaining</li>
                <li>Personal expenses</li>
                <li>Fines and penalties</li>
                <li>Political donations</li>
              </ul>

              <p className="highlight-note">
                Rule: Expenses must be "wholly and exclusively" for business. Mixed-use expenses (e.g., home office) can be partly claimed. When in doubt, <Link to="/contact">speak to an accountant</Link>.
              </p>

              <h2>Common Corporation Tax Mistakes</h2>

              <h3>Mistake 1: Missing the Payment Deadline</h3>

              <p>
                Many businesses confuse the filing deadline (12 months) with the payment deadline (9 months).
              </p>

              <p>
                <strong>Result:</strong> Interest charges accumulate from day 1 of being late.
              </p>

              <p>
                <strong>Solution:</strong> Set a calendar reminder for 8.5 months after year-end. Calculate your estimated tax and pay early.
              </p>

              <h3>Mistake 2: Claiming Non-Allowable Expenses</h3>

              <p>
                Claiming expenses that aren't allowable triggers HMRC enquiries.
              </p>

              <p>
                We see many West Midlands businesses incorrectly claim:
              </p>
              <ul>
                <li>Personal holidays as "business travel"</li>
                <li>Home broadband as 100% business (when it's mixed use)</li>
                <li>Expensive client meals as entertaining</li>
              </ul>

              <p>
                <strong>Solution:</strong> Only claim legitimate business expenses. Keep receipts and justification for every claim.
              </p>

              <h3>Mistake 3: Poor Record Keeping</h3>

              <p>
                If HMRC enquires and you can't provide evidence, they will disallow expenses and charge penalties.
              </p>

              <p>
                <strong>Solution:</strong> Maintain digital records from day one. Use accounting software to track all transactions.
              </p>

              <p>
                Full guide: <Link to="/blog/bookkeeping-compliance-uk">Bookkeeping Basics for Compliance</Link>
              </p>

              <h3>Mistake 4: Not Claiming Capital Allowances</h3>

              <p>
                Many businesses forget to claim capital allowances on equipment, vehicles, and machinery.
              </p>

              <p>
                <strong>Annual Investment Allowance (AIA):</strong> 100% first-year relief on qualifying assets up to £1 million.
              </p>

              <p>
                This is a huge tax saving opportunity — but you must claim it correctly.
              </p>

              <h2>What Triggers an HMRC Enquiry?</h2>

              <p>
                HMRC may investigate your Corporation Tax return if:
              </p>

              <ul>
                <li>Profits drop significantly year-on-year (without explanation)</li>
                <li>Expense claims seem unusually high for your industry</li>
                <li>You file late repeatedly</li>
                <li>Random selection (HMRC conducts random compliance checks)</li>
                <li>Tips from third parties (customers, suppliers, ex-employees)</li>
              </ul>

              <p>
                <strong>How to Avoid:</strong>
              </p>
              <ul>
                <li>File accurate returns on time</li>
                <li>Only claim allowable expenses</li>
                <li>Keep detailed records and evidence</li>
                <li>Work with a qualified accountant</li>
              </ul>

              <h2>Penalties for Non-Compliance</h2>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Issue</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Penalty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Late filing (up to 3 months)</td>
                    <td style={{ padding: '0.75rem' }}>£100</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Late filing (3-6 months)</td>
                    <td style={{ padding: '0.75rem' }}>£200</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Late filing (6+ months)</td>
                    <td style={{ padding: '0.75rem' }}>£200 + daily penalties</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>Late payment</td>
                    <td style={{ padding: '0.75rem' }}>Interest at 7.75%/year</td>
                  </tr>
                </tbody>
              </table>

              <p>
                Full guide: <Link to="/blog/hmrc-penalties-guide">HMRC Penalties Explained: How to Avoid Them</Link>
              </p>

              <h2>How Brightson Accounting Can Help</h2>

              <p>
                We help limited companies across Wolverhampton and the West Midlands with:
              </p>

              <ul>
                <li>Corporation Tax return preparation and filing</li>
                <li>Tax computation and planning (minimizing your bill legally)</li>
                <li>Accurate accounts preparation</li>
                <li>Capital allowances claims</li>
                <li>HMRC enquiry support</li>
              </ul>

              <p>
                Want to stay compliant and <Link to="/corporation-tax-calculator">reduce your corporation tax</Link> at the same time? We specialize in both.
              </p>

            </div>

            {/* End CTA */}
            <div className="article-cta-box" style={{ marginTop: '3rem' }}>
              <h2>🚀 Ready to stay compliant and reduce your tax bill?</h2>
              <p>We help businesses across Wolverhampton and the West Midlands:</p>
              <ul>
                <li>Stay compliant with HMRC</li>
                <li>Reduce tax legally</li>
                <li>Avoid penalties and investigations</li>
              </ul>
              <Link to="/contact" className="article-cta-button">👉 Book a Free Consultation</Link>
            </div>

            <div className="related-articles" style={{ marginTop: '3rem' }}>
              <h3>Related Guides</h3>
              <div className="related-links">
                <Link to="/blog/hmrc-compliance-guide-uk" className="related-link">HMRC Compliance Guide</Link>
                <Link to="/blog/hmrc-deadlines-uk" className="related-link">HMRC Deadlines</Link>
                <Link to="/blog/record-keeping-uk" className="related-link">Record Keeping</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default CorporationTaxCompliance;
